import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useEffect, useMemo, useState } from "react";
import {
	CategoryRounded,
	ColorLensRounded,
	CloseRounded,
	ChevronLeftRounded,
	ChevronRightRounded,
	DashboardRounded,
	DescriptionRounded,
	DirectionsRunRounded,
	FilterAltRounded,
	FlagRounded,
	Inventory2Rounded,
	PaletteRounded,
	SearchRounded,
	ShoppingBagRounded,
	StyleRounded,
	StraightenRounded,
	TimelineRounded,
} from "@mui/icons-material";
import { grey } from "@mui/material/colors";

const palette = {
	ink: "#111110",
	ink2: "#3a3a38",
	ink3: "#6b6b68",
	ink4: "#9b9b97",
	surface: "#fafaf8",
	surface2: "#f2f1ee",
	surface3: "#e8e7e2",
	border: "rgba(0,0,0,0.08)",
	border2: "rgba(0,0,0,0.14)",
	sidebar: "#141412",
	sidebar2: "#1e1e1b",
	sidebarText: "#c8c8c4",
	sidebarMuted: "#6a6a66",
	accent: "#c8a96e",
	green: "#2a7a4f",
	greenBg: "#eaf4ee",
	amber: "#b07a20",
	amberBg: "#fdf3e0",
	red: "#b83232",
	redBg: "#fdeaea",
	rr: "#111110",
	rm: "#7c3aed",
	ro: "#d97706",
	rz: "#0284c7",
};

const brandMeta = {
	all: { label: "All brands", count: "1,284", color: palette.accent },
	rr: { label: "Rare Rabbit", count: "412", color: palette.rr },
	rm: { label: "Rareism", count: "341", color: palette.rm },
	ro: { label: "Rareones", count: "218", color: palette.ro },
	rz: { label: "Rarez", count: "313", color: palette.rz },
};

const overviewMetrics = {
	all: { total: "1,284", pub: "847", wf: "312", draft: "125", comp: "78" },
	rr: { total: "412", pub: "298", wf: "81", draft: "33", comp: "82" },
	rm: { total: "341", pub: "214", wf: "79", draft: "48", comp: "76" },
	ro: { total: "218", pub: "129", wf: "61", draft: "28", comp: "71" },
	rz: { total: "313", pub: "206", wf: "91", draft: "16", comp: "79" },
};

const catData = {
	all: [
		{ cat: "SHIRT", total: 596, pub: 429, wf: 167, color: palette.rr },
		{ cat: "T-SHIRT", total: 313, pub: 206, wf: 107, color: palette.rz },
		{ cat: "JACKET", total: 148, pub: 90, wf: 58, color: palette.ro },
		{ cat: "DENIM PANT", total: 92, pub: 70, wf: 22, color: palette.green },
		{ cat: "POLO", total: 67, pub: 37, wf: 30, color: palette.amber },
		{ cat: "TROUSERS", total: 68, pub: 35, wf: 33, color: palette.red },
	],
	rr: [
		{ cat: "SHIRT", total: 296, pub: 221, wf: 75, color: palette.rr },
		{ cat: "T-SHIRT", total: 0, pub: 0, wf: 0, color: palette.rz },
		{ cat: "JACKET", total: 42, pub: 28, wf: 14, color: palette.ro },
		{ cat: "DENIM PANT", total: 18, pub: 16, wf: 2, color: palette.green },
		{ cat: "POLO", total: 0, pub: 0, wf: 0, color: palette.amber },
		{ cat: "TROUSERS", total: 56, pub: 33, wf: 23, color: palette.red },
	],
	rm: [
		{ cat: "SHIRT", total: 268, pub: 164, wf: 104, color: palette.rr },
		{ cat: "T-SHIRT", total: 0, pub: 0, wf: 0, color: palette.rz },
		{ cat: "JACKET", total: 54, pub: 31, wf: 23, color: palette.ro },
		{ cat: "DENIM PANT", total: 16, pub: 10, wf: 6, color: palette.green },
		{ cat: "POLO", total: 0, pub: 0, wf: 0, color: palette.amber },
		{ cat: "TROUSERS", total: 3, pub: 2, wf: 1, color: palette.red },
	],
	ro: [
		{ cat: "SHIRT", total: 52, pub: 28, wf: 24, color: palette.rr },
		{ cat: "T-SHIRT", total: 0, pub: 0, wf: 0, color: palette.rz },
		{ cat: "JACKET", total: 41, pub: 24, wf: 17, color: palette.ro },
		{ cat: "DENIM PANT", total: 0, pub: 0, wf: 0, color: palette.green },
		{ cat: "POLO", total: 38, pub: 19, wf: 19, color: palette.amber },
		{ cat: "TROUSERS", total: 87, pub: 58, wf: 29, color: palette.red },
	],
	rz: [
		{ cat: "SHIRT", total: 0, pub: 0, wf: 0, color: palette.rr },
		{ cat: "T-SHIRT", total: 313, pub: 206, wf: 107, color: palette.rz },
		{ cat: "JACKET", total: 11, pub: 7, wf: 4, color: palette.ro },
		{ cat: "DENIM PANT", total: 58, pub: 44, wf: 14, color: palette.green },
		{ cat: "POLO", total: 29, pub: 18, wf: 11, color: palette.amber },
		{ cat: "TROUSERS", total: 78, pub: 51, wf: 27, color: palette.red },
	],
};

const stageRows = [
	{ label: "Designer", value: 125, pct: 100, color: "#93c5fd" },
	{ label: "Fabric team", value: 93, pct: 74, color: "#6366f1" },
	{ label: "Trims team", value: 64, pct: 51, color: "#8B5CF6" },
	{ label: "Design head", value: 41, pct: 33, color: "#a78bfa" },
	{ label: "Published", value: 847, pct: 66, color: "#059669" },
];

const completenessRows = [
	{ label: "Basic info", pct: 97, color: "#059669", tone: "green" },
	{ label: "Fabric details", pct: 82, color: "#059669", tone: "green" },
	{ label: "Trims details", pct: 71, color: "#d97706", tone: "amber" },
	{ label: "Style color", pct: 61, color: "#d97706", tone: "amber" },
	{ label: "Media / images", pct: 54, color: "#dc2626", tone: "red" },
	{ label: "Design head", pct: 66, color: "#dc2626", tone: "red" },
];

const staleRows = [
	{ code: "RR-2481", name: "Oxford Stripe", stage: "Fabric", className: "ss-fab", days: 9 },
	{ code: "RM-1109", name: "Drape Kurti", stage: "Trims", className: "ss-tri", days: 14 },
	{ code: "RO-0391", name: "Kids Jogger", stage: "Fabric", className: "ss-fab", days: 11 },
	{ code: "RZ-0822", name: "Chunky Sole", stage: "Review", className: "ss-rev", days: 7 },
	{ code: "RR-2509", name: "Cargo Chino", stage: "Trims", className: "ss-tri", days: 18 },
];

const activityRows = [
	{
		title: 'RR-2479 "Linen Blazer" published',
		sub: "Rare Rabbit · Apparel · Design Head",
		time: "2m",
		bg: "#dcfce7",
		fg: "#14532d",
		icon: <FlagRounded />,
	},
	{
		title: "Trims added to RM-1140",
		sub: "Rareism · Floral Wrap Dress",
		time: "18m",
		bg: "#ede9fe",
		fg: "#5b21b6",
		icon: <StraightenRounded />,
	},
	{
		title: "Color assigned to RZ-0840",
		sub: "Rarez · Runner Pro · Midnight Navy",
		time: "34m",
		bg: "#dbeafe",
		fg: "#1e3a8a",
		icon: <ColorLensRounded />,
	},
	{
		title: "6 Rareones styles flagged",
		sub: "Missing fabric details · Auto-flag",
		time: "1h",
		bg: "#fef3c7",
		fg: "#78350f",
		icon: <FilterAltRounded />,
	},
];

const colorTiles = [
	{ name: "Blacks", cnt: 187, pct: "24%", swatch: "#1a1a1a", border: false },
	{ name: "Whites", cnt: 164, pct: "21%", swatch: "#f0f0ed", border: true },
	{ name: "Blues/Navy", cnt: 196, pct: "25%", swatch: "#1e3a5f", border: false },
	{ name: "Greens", cnt: 169, pct: "22%", swatch: "#6b7c4a", border: false },
	{ name: "Camel/Brown", cnt: 102, pct: "13%", swatch: "#c19a6b", border: false },
	{ name: "Burgundy", cnt: 89, pct: "11%", swatch: "#7b2d42", border: false },
	{ name: "Greys", cnt: 76, pct: "10%", swatch: "#7d8fa0", border: false },
	{ name: "Pinks", cnt: 68, pct: "9%", swatch: "#e8b4c0", border: false },
	{ name: "Yellows", cnt: 62, pct: "8%", swatch: "#f0c070", border: false },
	{ name: "Neutrals", cnt: 48, pct: "6%", swatch: "#e8e0d0", border: false },
	{ name: "Oranges", cnt: 31, pct: "4%", swatch: "#c97e3a", border: false },
	{ name: "Purples", cnt: 26, pct: "3%", swatch: "#9b7fd4", border: false },
];

const timelineRows = [
	{
		code: "RR-2479",
		name: "Linen Blazer",
		brand: "Rare Rabbit",
		cat: "Apparel",
		assignee: "Design Head",
		color: "Navy",
		ts: "2m",
		stage: "published",
		done: 5,
		overdue: false,
	},
	{
		code: "RM-1140",
		name: "Floral Wrap Dress",
		brand: "Rareism",
		cat: "Apparel",
		assignee: "Fabric Team",
		color: "Pink",
		ts: "18m",
		stage: "fabric",
		done: 2,
		overdue: false,
	},
	{
		code: "RO-0391",
		name: "Kids Jogger",
		brand: "Rareones",
		cat: "Apparel",
		assignee: "Trims Team",
		color: "Grey",
		ts: "34m",
		stage: "trims",
		done: 3,
		overdue: false,
	},
	{
		code: "RZ-0840",
		name: "Runner Pro",
		brand: "Rarez",
		cat: "Footwear",
		assignee: "Review",
		color: "Midnight Navy",
		ts: "1h",
		stage: "review",
		done: 4,
		overdue: true,
		days: 6,
	},
];

const techMeta = {
	shirt: { code: "RR-2479 · Slim Oxford Shirt · SS25", title: "Shirt / Apparel" },
	shoe: { code: "RZ-0840 · Runner Pro · SS25", title: "Footwear" },
	bag: { code: "RO-0391 · Crossbody Bag · SS25", title: "Bags" },
	belt: { code: "RR-2509 · Leather Belt · SS25", title: "Belt" },
	trouser: { code: "RM-1109 · Tailored Trouser · SS25", title: "Trousers" },
};

const stylesTableRows = [
	{ status: "Published", id: "RO-1", name: "ZYRA", season: "SS-25", order: "NEW", brand: "RAREONES", sub: "MAINLINE", div: "APPARELS", gender: "BOYS", category: "POLO", subcat: "SOLID", colors: 2, by: "meghad..." },
	{ status: "Published", id: "RO-10", name: "DRAX-K", season: "SS-25", order: "NEW", brand: "RAREONES", sub: "MAINLINE", div: "APPARELS", gender: "BOYS", category: "T-SHIRT", subcat: "PRINT", colors: 1, by: "maanasa..." },
	{ status: "Published", id: "RO-100", name: "ORIK", season: "SS-25", order: "NEW", brand: "RAREONES", sub: "MAINLINE", div: "APPARELS", gender: "BOYS", category: "POLO", subcat: "SOLID", colors: 2, by: "maanasa..." },
	{ status: "Published", id: "RO-101", name: "TROK", season: "SS-25", order: "NEW", brand: "RAREONES", sub: "MAINLINE", div: "APPARELS", gender: "BOYS", category: "SHIRT", subcat: "CHECKS", colors: 2, by: "vineetay..." },
	{ status: "Pending", id: "RO-102", name: "PINACO", season: "SS-25", order: "NEW", brand: "RAREONES", sub: "MAINLINE", div: "APPARELS", gender: "BOYS", category: "POLO", subcat: "SOLID", colors: 1, by: "maanasa..." },
	{ status: "Published", id: "RO-103", name: "PREST-K", season: "SS-25", order: "NEW", brand: "RAREONES", sub: "MAINLINE", div: "APPARELS", gender: "BOYS", category: "SHIRT", subcat: "SOLID", colors: 3, by: "vineetay..." },
	{ status: "Pending", id: "RO-104", name: "VOLTA", season: "FW-24", order: "NEW", brand: "RAREONES", sub: "MAINLINE", div: "APPARELS", gender: "GIRLS", category: "T-SHIRT", subcat: "PRINT", colors: 2, by: "adarsh..." },
	{ status: "Published", id: "RO-105", name: "NEXO", season: "SS-25", order: "REPEAT", brand: "RAREONES", sub: "DIFFUSION", div: "APPARELS", gender: "UNISEX", category: "POLO", subcat: "SOLID", colors: 4, by: "meghad..." },
	{ status: "Pending", id: "RO-106", name: "KRIX", season: "FW-24", order: "NEW", brand: "RAREONES", sub: "MAINLINE", div: "APPARELS", gender: "BOYS", category: "SHIRT", subcat: "CHECKS", colors: 1, by: "vineetay..." },
	{ status: "Published", id: "RO-107", name: "SLATO", season: "SS-25", order: "NEW", brand: "RAREONES", sub: "MAINLINE", div: "APPARELS", gender: "GIRLS", category: "T-SHIRT", subcat: "SOLID", colors: 3, by: "maanasa..." },
	{ status: "Pending", id: "RO-108", name: "DRAKO", season: "SS-25", order: "NEW", brand: "RAREONES", sub: "ARCHIVE", div: "APPARELS", gender: "BOYS", category: "POLO", subcat: "SOLID", colors: 2, by: "adarsh..." },
	{ status: "Published", id: "RO-109", name: "FLINT", season: "FW-24", order: "REPEAT", brand: "RAREONES", sub: "MAINLINE", div: "APPARELS", gender: "BOYS", category: "SHIRT", subcat: "PRINT", colors: 1, by: "meghad..." },
	{ status: "Published", id: "RO-110", name: "AXOR", season: "SS-25", order: "NEW", brand: "RAREONES", sub: "MAINLINE", div: "APPARELS", gender: "UNISEX", category: "T-SHIRT", subcat: "SOLID", colors: 2, by: "vineetay..." },
	{ status: "Pending", id: "RO-111", name: "CRUX", season: "SS-25", order: "NEW", brand: "RAREONES", sub: "DIFFUSION", div: "APPARELS", gender: "GIRLS", category: "POLO", subcat: "CHECKS", colors: 3, by: "maanasa..." },
	{ status: "Published", id: "RO-112", name: "VORN", season: "FW-24", order: "NEW", brand: "RAREONES", sub: "MAINLINE", div: "APPARELS", gender: "BOYS", category: "SHIRT", subcat: "SOLID", colors: 2, by: "adarsh..." },
	{ status: "Pending", id: "RO-113", name: "STRIX", season: "SS-25", order: "NEW", brand: "RAREONES", sub: "MAINLINE", div: "APPARELS", gender: "BOYS", category: "T-SHIRT", subcat: "PRINT", colors: 1, by: "vineetay..." },
	{ status: "Published", id: "RO-114", name: "POLKA", season: "SS-25", order: "REPEAT", brand: "RAREONES", sub: "ARCHIVE", div: "APPARELS", gender: "GIRLS", category: "SHIRT", subcat: "CHECKS", colors: 4, by: "meghad..." },
	{ status: "Pending", id: "RO-115", name: "ZENTA", season: "FW-24", order: "NEW", brand: "RAREONES", sub: "MAINLINE", div: "APPARELS", gender: "BOYS", category: "POLO", subcat: "SOLID", colors: 2, by: "maanasa..." },
	{ status: "Published", id: "RO-116", name: "RONIX", season: "SS-25", order: "NEW", brand: "RAREONES", sub: "MAINLINE", div: "APPARELS", gender: "UNISEX", category: "T-SHIRT", subcat: "SOLID", colors: 3, by: "adarsh..." },
	{ status: "Pending", id: "RO-117", name: "GRAVO", season: "SS-25", order: "NEW", brand: "RAREONES", sub: "DIFFUSION", div: "APPARELS", gender: "BOYS", category: "SHIRT", subcat: "PRINT", colors: 1, by: "vineetay..." },
];

const reportNav = [
	{ key: "overview", label: "Overview", icon: <DashboardRounded /> },
	{ key: "categories", label: "Categories", icon: <CategoryRounded /> },
	{ key: "colors", label: "Colors", icon: <PaletteRounded /> },
	{ key: "timeline", label: "Timeline", icon: <TimelineRounded /> },
	{ key: "techview", label: "Tech View", icon: <StraightenRounded /> },
];

const brandTabs = [
	{ key: "all", label: "All brands", dot: palette.accent },
	{ key: "rr", label: "Rare Rabbit", dot: palette.rr },
	{ key: "rm", label: "Rareism", dot: palette.rm },
	{ key: "ro", label: "Rareones", dot: palette.ro },
	{ key: "rz", label: "Rarez", dot: palette.rz },
];

const stageFilters = [
	{ key: "all", label: "All stages" },
	{ key: "design", label: "Design" },
	{ key: "fabric", label: "Fabric" },
	{ key: "trims", label: "Trims" },
	{ key: "review", label: "Review" },
	{ key: "published", label: "Published" },
	{ key: "overdue", label: "Overdue" },
];

const techTabs = [
	{ key: "shirt", label: "Shirt / Apparel", icon: <StyleRounded /> },
	{ key: "shoe", label: "Footwear", icon: <DirectionsRunRounded /> },
	{ key: "bag", label: "Bags", icon: <ShoppingBagRounded /> },
	{ key: "belt", label: "Belt", icon: <Inventory2Rounded /> },
	{ key: "trouser", label: "Trousers", icon: <DescriptionRounded /> },
];

const sectionButtonSx = {
	fontSize: 11,
	py: 1,
	px: 1.25,
	minWidth: 0,
	justifyContent: "flex-start",
	borderRadius: 1,
	textTransform: "none",
	color: palette.sidebarMuted,
	"&.Mui-selected": {
		bgcolor: "rgba(200,169,110,0.15)",
		color: palette.accent,
	},
};

function cardSx() {
	return {
		bgcolor: "#fff",
		border: `1px solid ${palette.border}`,
		borderRadius: 2,
		p: 2.25,
		boxShadow: "none",
	};
}

function BarRow({ label, value, pct, color, valueWidth = 56, pctWidth = 40, labelWidth = 90 }) {
	return (
		<Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 1.05 }}>
			<Typography sx={{ fontSize: 11, color: palette.ink3, width: labelWidth, flexShrink: 0 }}>{label}</Typography>
			<Box sx={{ flex: 1, height: 7, bgcolor: palette.surface2, borderRadius: 999, overflow: "hidden" }}>
				<Box sx={{ width: `${pct}%`, height: "100%", bgcolor: color, borderRadius: 999 }} />
			</Box>
			<Typography
				sx={{
					fontSize: 11,
					color: palette.ink2,
					fontFamily: "Roboto, sans-serif",
					fontWeight: 500,
					width: valueWidth,
					textAlign: "right",
				}}>
				{value}
			</Typography>
			<Typography sx={{ fontSize: 10, color: palette.ink4, width: pctWidth, textAlign: "right" }}>{pct}%</Typography>
		</Box>
	);
}

function MetricCard({ label, value, delta, deltaColor = palette.ink3 }) {
	return (
		<Box sx={{ bgcolor: "#fff", border: `1px solid ${palette.border}`, borderRadius: 2, p: 1.75 }}>
			<Typography sx={{ fontSize: 10, color: palette.ink3, textTransform: "uppercase", letterSpacing: "0.06em", mb: 1 }}>
				{label}
			</Typography>
			<Typography sx={{ fontFamily: "Roboto, sans-serif", fontSize: 28, fontWeight: 700, color: palette.ink, lineHeight: 1 }}>
				{value}
			</Typography>
			<Typography sx={{ fontSize: 10, mt: 0.75, color: deltaColor }}>{delta}</Typography>
		</Box>
	);
}

function SectionTitle({ title, sub }) {
	return (
		<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5, mb: 1.5 }}>
			<Typography sx={{ fontSize: 12, fontWeight: 600, color: palette.ink }}>{title}</Typography>
			{sub ? <Typography sx={{ fontSize: 10, color: palette.ink4 }}>{sub}</Typography> : null}
		</Box>
	);
}

function PageNav({ activePage, setActivePage }) {
	return (
		<Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
			{reportNav.map((item) => (
				<Box
					key={item.key}
					component="button"
					onClick={() => setActivePage(item.key)}
					sx={{
						fontSize: 11,
						px: 1.5,
						py: 0.8,
						borderRadius: 1,
						textTransform: "none",
						border: `1px solid ${palette.border2}`,
						bgcolor: activePage === item.key ? palette.ink : "#fff",
						color: activePage === item.key ? "#fff" : palette.ink3,
						display: "flex",
						alignItems: "center",
						gap: 0.75,
						cursor: "pointer",
					}}>
					<Box sx={{ display: "inline-flex", alignItems: "center", fontSize: 16 }}>{item.icon}</Box>
					{item.label}
				</Box>
			))}
		</Stack>
	);
}

function TechDrawing({ active }) {
	const meta = techMeta[active];
	if (active === "shoe") {
		return (
			<Box sx={{ width: "100%", bgcolor: "#f9f8f6", borderRadius: 1, p: 2, minHeight: 280 }}>
				<Typography sx={{ fontSize: 10, color: palette.ink4, textAlign: "center", mb: 1 }}>LATERAL VIEW</Typography>
				<svg viewBox="0 0 380 280" width="100%" height="auto">
					<rect width="380" height="280" rx="8" fill="#f9f8f6" />
					<path d="M50,220 Q60,240 120,245 L300,245 Q350,245 340,225 L330,220 Z" fill="#3a3a38" />
					<path d="M55,210 Q65,225 120,228 L300,228 Q348,228 338,212 L320,210 Z" fill="#d8d7d2" />
					<path d="M80,210 L75,160 Q70,100 120,80 L240,75 Q300,72 320,100 L330,210 Z" fill="#1e3a5f" />
					<path d="M80,210 L75,160 Q72,120 100,100 L130,95 Q100,130 100,210 Z" fill="#162d4d" />
					<path d="M160,80 L200,78 L220,100 L200,115 L160,112 L145,95 Z" fill="white" opacity="0.15" />
					<path d="M185,80 L215,80 L218,160 L182,160 Z" fill="#2a4f7c" />
					<path d="M160,170 L240,170 Q250,160 245,150 L155,150 Q150,160 160,170 Z" fill="#2a4f7c" />
					<text x="270" y="175" fontSize="9" fill="white" opacity="0.4">
						RAREZ
					</text>
				</svg>
			</Box>
		);
	}

	return (
		<Box sx={{ width: "100%", bgcolor: "#f9f8f6", borderRadius: 1, p: 2, minHeight: 280 }}>
			<Typography sx={{ fontSize: 10, color: palette.ink4, textAlign: "center", mb: 1 }}>{meta.title.toUpperCase()}</Typography>
			<svg viewBox="0 0 380 420" width="100%" height="auto">
				<rect width="380" height="420" rx="8" fill="#f9f8f6" />
				<path
					d="M120,100 L85,120 L60,160 L60,360 L320,360 L320,160 L295,120 L260,100 Z"
					fill="#e8e7e2"
					stroke="#3a3a38"
					strokeWidth="1.5"
				/>
				<path
					d="M120,100 L85,120 L60,160 L30,200 L20,260 L50,270 L80,200 L100,170 L120,150 Z"
					fill="#e8e7e2"
					stroke="#3a3a38"
					strokeWidth="1.5"
				/>
				<path
					d="M260,100 L295,120 L320,160 L350,200 L360,260 L330,270 L300,200 L280,170 L260,150 Z"
					fill="#e8e7e2"
					stroke="#3a3a38"
					strokeWidth="1.5"
				/>
				<path d="M190,60 L150,100 L165,115 L190,90 Z" fill="#d8d7d2" />
				<path d="M190,60 L230,100 L215,115 L190,90 Z" fill="#d8d7d2" />
				<line x1="190" y1="95" x2="190" y2="360" stroke="#6b6b68" strokeWidth="1" strokeDasharray="3,3" />
				{[130, 165, 200, 235, 270, 310].map((y) => (
					<circle key={y} cx="190" cy={y} r="4" fill="#c8a96e" stroke="#9a7a4e" strokeWidth="1" />
				))}
			</svg>
		</Box>
	);
}

const StylesReport = () => {
	const [activePage, setActivePage] = useState("overview");
	const [activeBrand, setActiveBrand] = useState("all");
	const [timelineFilter, setTimelineFilter] = useState("all");
	const [activeTech, setActiveTech] = useState("shirt");
	const [tableFilters, setTableFilters] = useState({
		status: null,
		season: null,
		gender: null,
		category: null,
	});
	const [tableSearch, setTableSearch] = useState("");
	const [sortCol, setSortCol] = useState(null);
	const [sortDir, setSortDir] = useState(1);
	const [tablePage, setTablePage] = useState(1);
	const [selectedRows, setSelectedRows] = useState(() => new Set());
	const [showImages, setShowImages] = useState(false);
	const [remarkOpen, setRemarkOpen] = useState(false);
	const [remarkText, setRemarkText] = useState("");

	const metrics = overviewMetrics[activeBrand];

	const filteredCatRows = useMemo(() => catData[activeBrand] || catData.all, [activeBrand]);
	const filteredTimeline = useMemo(() => {
		return timelineRows.filter((row) => {
			if (timelineFilter === "all") return true;
			if (timelineFilter === "overdue") return row.overdue;
			return row.stage === timelineFilter;
		});
	}, [timelineFilter]);
	const filteredTableRows = useMemo(() => {
		const q = tableSearch.trim().toLowerCase();
		let rows = stylesTableRows.filter((row) => {
			if (tableFilters.status && row.status !== tableFilters.status) return false;
			if (tableFilters.season && row.season !== tableFilters.season) return false;
			if (tableFilters.gender && row.gender !== tableFilters.gender) return false;
			if (tableFilters.category && row.category !== tableFilters.category) return false;
			if (q && !row.id.toLowerCase().includes(q) && !row.name.toLowerCase().includes(q)) return false;
			return true;
		});
		if (sortCol) {
			rows = [...rows].sort((a, b) => {
				const av = `${a[sortCol] ?? ""}`.toLowerCase();
				const bv = `${b[sortCol] ?? ""}`.toLowerCase();
				if (av < bv) return -sortDir;
				if (av > bv) return sortDir;
				return 0;
			});
		}
		return rows;
	}, [sortCol, sortDir, tableFilters, tableSearch]);
	const tablePageSize = 10;
	const tablePageCount = Math.max(1, Math.ceil(filteredTableRows.length / tablePageSize));
	const tableStart = (tablePage - 1) * tablePageSize;
	const visibleTableRows = filteredTableRows.slice(tableStart, tableStart + tablePageSize);
	const currentPageIds = visibleTableRows.map((row) => row.id);
	const allCurrentSelected = currentPageIds.length > 0 && currentPageIds.every((id) => selectedRows.has(id));
	const activeChips = Object.entries(tableFilters).filter(([, value]) => value);
	const tableGridTemplate = showImages
		? "36px 84px 52px 84px 112px 120px 100px 94px 90px 96px 96px 90px 92px 90px 60px 110px"
		: "36px 84px 84px 112px 120px 100px 94px 90px 96px 96px 90px 92px 90px 60px 110px";

	useEffect(() => {
		setSelectedRows(new Set());
		setTablePage(1);
	}, [sortCol, sortDir, tableFilters, tableSearch]);

	useEffect(() => {
		setTablePage((prev) => Math.min(prev, tablePageCount));
	}, [tablePageCount]);

	const toggleFilter = (group, value) => {
		setTableFilters((prev) => ({
			...prev,
			[group]: prev[group] === value ? null : value,
		}));
	};

	const clearFilter = (group) => {
		setTableFilters((prev) => ({
			...prev,
			[group]: null,
		}));
	};

	const clearAllTableFilters = () => {
		setTableFilters({ status: null, season: null, gender: null, category: null });
		setTableSearch("");
		setSortCol(null);
		setSortDir(1);
		setSelectedRows(new Set());
		setTablePage(1);
	};

	const sortBy = (col) => {
		setSortDir((prev) => (sortCol === col ? prev * -1 : 1));
		setSortCol(col);
	};

	const toggleRow = (id) => {
		setSelectedRows((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	};

	const toggleAllRows = (checked) => {
		setSelectedRows((prev) => {
			const next = new Set(prev);
			visibleTableRows.forEach((row) => {
				if (checked) next.add(row.id);
				else next.delete(row.id);
			});
			return next;
		});
	};

	const clearSelection = () => setSelectedRows(new Set());

	const openRemarkModal = () => {
		setRemarkText("");
		setRemarkOpen(true);
	};

	const closeRemarkModal = () => setRemarkOpen(false);

	const appendRemarkTag = (tag) => {
		setRemarkText((prev) => (prev ? `${prev}, ${tag}` : tag));
	};

	const submitRemark = () => {
		setRemarkOpen(false);
		setRemarkText("");
	};

	return (
		<Box
			sx={{
				display: "block",
				bgcolor: palette.surface,
				color: palette.ink,
				minHeight: "calc(100vh - 170px)",
				borderRadius: 2,
				overflow: "hidden",
				border: `1px solid ${palette.border}`,
			}}>
			<Box
				sx={{
					display: "none",
					bgcolor: grey[900],
					color: palette.sidebarText,
					p: 2,
					display: "none",
					flexDirection: "column",
					gap: 2,
					borderRight: { xs: "none", lg: "1px solid rgba(255,255,255,0.06)" },
				}}>
				<Box sx={{ borderBottom: "1px solid rgba(255,255,255,0.06)", pb: 1.5 }}>
					<Typography
						sx={{
							fontSize: 9,
							letterSpacing: "0.1em",
							textTransform: "uppercase",
							color: palette.sidebarMuted,
							mb: 1.25,
						}}>
						Brands
					</Typography>
					<Stack spacing={0.25}>
						{brandTabs.map((brand) => (
							<Box
								key={brand.key}
								onClick={() => setActiveBrand(brand.key)}
								sx={{
									display: "flex",
									alignItems: "center",
									gap: 1,
									p: 1,
									borderRadius: 1,
									cursor: "pointer",
									bgcolor: activeBrand === brand.key ? "rgba(255,255,255,0.07)" : "transparent",
									"&:hover": { bgcolor: "rgba(255,255,255,0.05)" },
								}}>
								<Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: brand.dot, flexShrink: 0 }} />
								<Typography sx={{ fontSize: 12, color: "white" }}>{brand.label}</Typography>
								<Typography
									sx={{
										ml: "auto",
										fontSize: 10,
										fontFamily: "Roboto, sans-serif",
										fontWeight: 500,
										color: "white",
									}}>
									{brand.count}
								</Typography>
							</Box>
						))}
					</Stack>
				</Box>

				<Box sx={{ flex: 1 }}>
					<Typography
						sx={{
							fontSize: 9,
							letterSpacing: "0.1em",
							textTransform: "uppercase",
							color: palette.sidebarMuted,
							mb: 1.25,
						}}>
						Navigation
					</Typography>
					<Stack spacing={0.4}>
						{reportNav.map((item) => (
							<Box
								component="button"
								key={item.key}
								onClick={() => setActivePage(item.key)}
								sx={{
									...sectionButtonSx,
									display: "flex",
									alignItems: "center",
									gap: 1,
									width: "100%",
									border: "none",
									background: "transparent",
									cursor: "pointer",
									bgcolor: activePage === item.key ? "rgba(200,169,110,0.15)" : "transparent",
									color: "white",
								}}>
								<Box sx={{ display: "inline-flex", alignItems: "center", fontSize: 16 }}>{item.icon}</Box>
								<span>{item.label}</span>
							</Box>
						))}
					</Stack>
				</Box>

				<Box sx={{ pt: 2, borderTop: "1px solid rgba(255,255,255,0.06)", fontSize: 10, color: palette.sidebarMuted }}>
					Season SS25 · Updated now
				</Box>
			</Box>

			<Box sx={{ p: { xs: 2, md: 3 }, width: "100%", boxSizing: "border-box" }}>
				{activePage === "overview" && (
					<Stack spacing={3}>
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								justifyContent: "space-between",
								gap: 2,
								flexWrap: "wrap",
							}}>
							<Box>
								<Typography sx={{ fontSize: 26, fontWeight: 700, color: palette.ink, lineHeight: 1.1 }}>
									Style Health Report
								</Typography>
								<Typography sx={{ fontSize: 12, color: palette.ink3, mt: 0.5 }}>
									Real-time overview across all 4 brands Â· SS25
								</Typography>
							</Box>
							<PageNav activePage={activePage} setActivePage={setActivePage} />
						</Box>
						<Box
							sx={{
								display: "grid",
								gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" },
								gap: 1.25,
							}}>
							<MetricCard label="Total styles" value="3,496" delta="across all brands" deltaColor={palette.ink3} />
							<MetricCard label="Published" value="1,563" delta="44.7% of total" deltaColor={palette.green} />
							<MetricCard label="Pending" value="1,933" delta="55.3% of total" deltaColor={palette.red} />
							<MetricCard label="Avg MRP" value="₹1,240" delta="across all styles" deltaColor={palette.ink3} />
						</Box>

						<Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 1.75 }}>
							<Box sx={cardSx()}>
								<SectionTitle title="Brand wise published" sub="Published vs pending by brand" />
								<Box sx={{ display: "flex", gap: 2, mb: 1.5, flexWrap: "wrap" }}>
									<Box sx={{ display: "flex", alignItems: "center", gap: 0.75, fontSize: 10, color: palette.ink3 }}>
										<Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: "#2a7a4f" }} />
										Published
									</Box>
									<Box sx={{ display: "flex", alignItems: "center", gap: 0.75, fontSize: 10, color: palette.ink3 }}>
										<Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: "#f09595" }} />
										Pending
									</Box>
								</Box>
								{[
									["Rare Rabbit", 412, 298, "#111110"],
									["Rareism", 341, 214, "#7c3aed"],
									["Rareones", 218, 129, "#d97706"],
									["Rarez", 313, 206, "#0284c7"],
								].map(([label, total, pub, color]) => {
									const pending = total - pub;
									const publishedWidth = Math.max(18, Math.round((pub / 412) * 100));
									const pendingWidth = Math.max(10, Math.round((pending / 412) * 100));
									return (
										<Box key={label} sx={{ mb: 1.2 }}>
											<Box
												sx={{
													display: "flex",
													justifyContent: "space-between",
													mb: 0.55,
													fontSize: 11,
													color: palette.ink2,
												}}>
												<Box sx={{ fontWeight: 600 }}>{label}</Box>
												<Box>
													{pub} / {pending}
												</Box>
											</Box>
											<Box
												sx={{
													display: "flex",
													height: 10,
													borderRadius: 999,
													overflow: "hidden",
													bgcolor: palette.surface2,
												}}>
												<Box sx={{ width: `${publishedWidth}%`, bgcolor: color }} />
												<Box sx={{ width: `${pendingWidth}%`, bgcolor: "#f3b0b0" }} />
											</Box>
										</Box>
									);
								})}
							</Box>

							<Box sx={cardSx()}>
								<SectionTitle title="Online vs offline" sub="Published style distribution by channel" />
								<Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" }, gap: 1.25 }}>
									{[
										{
											label: "Online styles",
											total: "892",
											iconColor: "#185FA5",
											bg: "#eaf3fb",
											rows: [
												["Published", "612", palette.green],
												["Pending", "280", palette.red],
												["Rate", "57%", palette.ink2],
											],
										},
										{
											label: "Offline styles",
											total: "671",
											iconColor: "#7f6ccf",
											bg: "#f0ecfb",
											rows: [
												["Published", "951", palette.green],
												["Pending", "1653", palette.red],
												["Rate", "43%", palette.ink2],
											],
										},
									].map((item) => (
										<Box
											key={item.label}
											sx={{ border: `1px solid ${palette.border}`, borderRadius: 1.5, p: 1.5, bgcolor: "#fff" }}>
											<Box
												sx={{
													width: 34,
													height: 34,
													borderRadius: "50%",
													display: "grid",
													placeItems: "center",
													bgcolor: item.bg,
													color: item.iconColor,
													fontSize: 16,
													mb: 1,
												}}>
												<Inventory2Rounded fontSize="inherit" />
											</Box>
											<Typography sx={{ fontSize: 22, fontWeight: 700, color: item.iconColor, lineHeight: 1 }}>
												{item.total}
											</Typography>
											<Typography sx={{ fontSize: 11, color: palette.ink2, mb: 1 }}>{item.label}</Typography>
											<Box sx={{ display: "flex", flexDirection: "column", gap: 0.7 }}>
												{item.rows.map(([label, value, tone]) => (
													<Box
														key={label}
														sx={{ display: "flex", justifyContent: "space-between", fontSize: 10 }}>
														<Box sx={{ color: palette.ink3 }}>{label}</Box>
														<Box sx={{ color: tone, fontWeight: 700 }}>{value}</Box>
													</Box>
												))}
											</Box>
										</Box>
									))}
								</Box>
							</Box>
						</Box>

						<Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 1.75 }}>
							<Box sx={cardSx()}>
								<SectionTitle title="Section wise" sub="Styles by division & gender" />
								<Box sx={{ display: "flex", gap: 2, mb: 1.5, flexWrap: "wrap" }}>
									<Box sx={{ display: "flex", alignItems: "center", gap: 0.75, fontSize: 10, color: palette.ink3 }}>
										<Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: "#0C447C" }} />
										Published
									</Box>
									<Box sx={{ display: "flex", alignItems: "center", gap: 0.75, fontSize: 10, color: palette.ink3 }}>
										<Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: "#B5D4F4" }} />
										Pending
									</Box>
								</Box>
								{[
									["Boys Apparels", 520, 460],
									["Girls Apparels", 410, 330],
									["Unisex", 230, 190],
									["Men", 210, 170],
									["Women", 180, 140],
									["Kids", 150, 130],
								].map(([label, pub, pend]) => {
									const total = pub + pend;
									const pubWidth = Math.round((pub / 520) * 100);
									const pendWidth = Math.round((pend / 520) * 100);
									return (
										<Box key={label} sx={{ mb: 1.2 }}>
											<Typography sx={{ fontSize: 11, color: palette.ink2, mb: 0.5 }}>{label}</Typography>
											<Box
												sx={{
													display: "flex",
													height: 12,
													borderRadius: 999,
													overflow: "hidden",
													bgcolor: palette.surface2,
												}}>
												<Box sx={{ width: `${pubWidth}%`, bgcolor: "#0C447C" }} />
												<Box sx={{ width: `${pendWidth}%`, bgcolor: "#B5D4F4" }} />
											</Box>
											<Box
												sx={{
													display: "flex",
													justifyContent: "space-between",
													mt: 0.4,
													fontSize: 10,
													color: palette.ink3,
												}}>
												<Box>{pub} published</Box>
												<Box>{pend} pending</Box>
												<Box>{total}</Box>
											</Box>
										</Box>
									);
								})}
							</Box>

							<Box sx={cardSx()}>
								<SectionTitle title="MRP range wise" sub="Style count per price band" />
								{[
									["₹299 - ₹499", 290],
									["₹500 - ₹999", 650],
									["₹1,000 - ₹1,499", 920],
									["₹1,500 - ₹1,999", 540],
									["₹2,000 - ₹2,999", 420],
									["₹3,000+", 320],
								].map(([label, count]) => {
									const width = Math.round((count / 920) * 100);
									return (
										<Box key={label} sx={{ mb: 1.15 }}>
											<Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.45, fontSize: 11 }}>
												<Box sx={{ color: palette.ink2 }}>{label}</Box>
												<Box sx={{ color: palette.ink, fontWeight: 700 }}>{count}</Box>
											</Box>
											<Box sx={{ height: 10, borderRadius: 999, overflow: "hidden", bgcolor: palette.surface2 }}>
												<Box sx={{ width: `${width}%`, height: "100%", bgcolor: palette.accent }} />
											</Box>
										</Box>
									);
								})}
								<Box
									sx={{
										display: "flex",
										justifyContent: "space-between",
										gap: 1.5,
										pt: 1.25,
										mt: 1.25,
										borderTop: `1px solid ${palette.border}`,
										fontSize: 10,
									}}>
									<Box>
										<Box sx={{ color: palette.ink4 }}>Min MRP</Box>
										<Box sx={{ fontWeight: 700, color: palette.ink }}>₹299</Box>
									</Box>
									<Box>
										<Box sx={{ color: palette.ink4 }}>Median MRP</Box>
										<Box sx={{ fontWeight: 700, color: palette.ink }}>₹999</Box>
									</Box>
									<Box>
										<Box sx={{ color: palette.ink4 }}>Max MRP</Box>
										<Box sx={{ fontWeight: 700, color: palette.ink }}>₹4,999</Box>
									</Box>
								</Box>
							</Box>
							<Box sx={cardSx()}>
								<SectionTitle title="Category wise published" sub="Top 10 categories - published vs pending" />
								<Box sx={{ display: "flex", gap: 2, mb: 1.5, flexWrap: "wrap" }}>
									<Box sx={{ display: "flex", alignItems: "center", gap: 0.75, fontSize: 10, color: palette.ink3 }}>
										<Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: "#0C447C" }} />
										Published
									</Box>
									<Box sx={{ display: "flex", alignItems: "center", gap: 0.75, fontSize: 10, color: palette.ink3 }}>
										<Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: "#B5D4F4" }} />
										Pending
									</Box>
								</Box>
								{[
									["Polo", 390, 210],
									["T-Shirt", 340, 200],
									["Shirt", 290, 180],
									["Denim", 180, 140],
									["Dress", 140, 90],
									["Jacket", 110, 80],
									["Shorts", 90, 60],
									["Sweatshirt", 80, 50],
									["Kurti", 70, 40],
									["Other", 60, 30],
								].map(([label, pub, pend]) => {
									const total = pub + pend;
									const pubWidth = Math.round((pub / 390) * 100);
									const pendWidth = Math.round((pend / 390) * 100);
									return (
										<Box key={label} sx={{ mb: 1.05 }}>
											<Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.4, fontSize: 11 }}>
												<Box sx={{ color: palette.ink2 }}>{label}</Box>
												<Box sx={{ color: palette.ink, fontWeight: 700 }}>
													{pub} / {pend}
												</Box>
											</Box>
											<Box
												sx={{
													display: "flex",
													height: 11,
													borderRadius: 999,
													overflow: "hidden",
													bgcolor: palette.surface2,
												}}>
												<Box sx={{ width: `${pubWidth}%`, bgcolor: "#0C447C" }} />
												<Box sx={{ width: `${pendWidth}%`, bgcolor: "#B5D4F4" }} />
											</Box>
											<Box
												sx={{
													display: "flex",
													justifyContent: "space-between",
													mt: 0.35,
													fontSize: 10,
													color: palette.ink4,
												}}>
												<Box>{total} total</Box>
												<Box>{Math.round((pub / total) * 100)}% published</Box>
											</Box>
										</Box>
									);
								})}
							</Box>
						</Box>

						<Box sx={cardSx()}>
							<Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
								<Box
									sx={{
										display: "flex",
										alignItems: "center",
										justifyContent: "space-between",
										gap: 2,
										flexWrap: "wrap",
										pb: 1.25,
										borderBottom: `1px solid ${palette.border}`,
									}}>
									<Box sx={{ display: "flex", alignItems: "center", gap: 1.25, flexWrap: "wrap" }}>
										<Box
											sx={{
												display: "flex",
												alignItems: "center",
												gap: 1,
												px: 1.2,
												py: 0.85,
												bgcolor: "#fff",
												border: `1px solid ${palette.border}`,
												borderRadius: 1.5,
												minWidth: { xs: "100%", md: 320 },
											}}>
											<SearchRounded sx={{ fontSize: 18, color: palette.ink4, flexShrink: 0 }} />
											<Box
												component="input"
												value={tableSearch}
												onChange={(e) => setTableSearch(e.target.value)}
												placeholder="Search style ID or name..."
												sx={{
													border: "none",
													outline: "none",
													width: "100%",
													fontSize: 12,
													fontFamily: "Roboto, sans-serif",
													color: palette.ink,
													bgcolor: "transparent",
													"&::placeholder": { color: palette.ink4, opacity: 1 },
												}}
											/>
										</Box>
										<Box
											component="button"
											onClick={clearAllTableFilters}
											sx={{
												display: "inline-flex",
												alignItems: "center",
												gap: 0.7,
												fontSize: 11,
												px: 1.2,
												py: 0.8,
												borderRadius: 1,
												border: `1px solid ${palette.border2}`,
												bgcolor: "#fff",
												color: palette.ink2,
												cursor: "pointer",
											}}>
											<CloseRounded sx={{ fontSize: 15 }} />
											Clear
										</Box>
									</Box>
								</Box>

								<Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center" }}>
									{[
										{ label: "Status", key: "status", values: ["Published", "Pending"] },
										{ label: "Season", key: "season", values: ["SS-25", "FW-24"] },
										{ label: "Gender", key: "gender", values: ["BOYS", "GIRLS", "UNISEX"] },
										{ label: "Category", key: "category", values: ["POLO", "T-SHIRT", "SHIRT"] },
									].map((group) => (
										<Box key={group.key} sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
											<Typography sx={{ fontSize: 11, color: palette.ink4, fontWeight: 600 }}>{group.label}</Typography>
											{group.values.map((value) => {
												const active = tableFilters[group.key] === value;
												return (
													<Box
														key={value}
														component="button"
														onClick={() => toggleFilter(group.key, value)}
														sx={{
															fontSize: 10,
															px: 1.1,
															py: 0.55,
															borderRadius: 999,
															border: `1px solid ${active ? palette.ink : palette.border2}`,
															bgcolor: active ? palette.ink : "#fff",
															color: active ? "#fff" : palette.ink3,
															cursor: "pointer",
														}}>
														{value}
													</Box>
												);
											})}
										</Box>
									))}
								</Box>

								<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
									{activeChips.map(([key, value]) => (
										<Box
											key={key}
											sx={{
												display: "inline-flex",
												alignItems: "center",
												gap: 0.7,
												fontSize: 11,
												px: 1,
												py: 0.5,
												borderRadius: 999,
												bgcolor: palette.surface2,
												color: palette.ink2,
											}}>
											{key}: <Box component="strong">{value}</Box>
											<Box
												component="button"
												onClick={() => clearFilter(key)}
												sx={{
													border: "none",
													bgcolor: "transparent",
													cursor: "pointer",
													display: "inline-flex",
													alignItems: "center",
													color: palette.ink4,
													p: 0,
												}}>
												<CloseRounded sx={{ fontSize: 14 }} />
											</Box>
										</Box>
									))}
								</Box>

								{remarkOpen && (
									<Box
										sx={{
											position: "fixed",
											inset: 0,
											bgcolor: "rgba(0,0,0,0.45)",
											zIndex: 1300,
											display: "grid",
											placeItems: "center",
											p: 2,
										}}>
										<Box
											sx={{
												width: "min(720px, 100%)",
												bgcolor: "#fff",
												borderRadius: 2,
												boxShadow: "0 20px 60px rgba(0,0,0,0.22)",
												border: `1px solid ${palette.border}`,
												p: 2,
											}}>
											<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, mb: 1.25 }}>
												<Typography sx={{ fontSize: 14, fontWeight: 700, color: palette.ink }}>Add remark</Typography>
												<Box
													component="button"
													onClick={closeRemarkModal}
													sx={{ border: "none", bgcolor: "transparent", cursor: "pointer", color: palette.ink4 }}>
													<CloseRounded />
												</Box>
											</Box>
											<Typography sx={{ fontSize: 11, color: palette.ink3, mb: 1 }}>
												Adding remark to <strong>{selectedRows.size}</strong> selected styles
											</Typography>
											<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1.25 }}>
												{[...selectedRows].map((id) => (
													<Box key={id} sx={{ fontSize: 10, px: 0.9, py: 0.4, borderRadius: 999, bgcolor: palette.surface2 }}>
														{id}
													</Box>
												))}
											</Box>
											<Typography sx={{ fontSize: 11, fontWeight: 600, color: palette.ink, mb: 0.75 }}>Quick tags</Typography>
											<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 1.25 }}>
												{["Needs review", "Priority", "Hold", "Approved", "Rework required"].map((tag) => (
													<Box
														key={tag}
														component="button"
														onClick={() => appendRemarkTag(tag)}
														sx={{
															fontSize: 11,
															px: 1.05,
															py: 0.55,
															borderRadius: 999,
															border: `1px solid ${palette.border2}`,
															bgcolor: "#fff",
															cursor: "pointer",
															color: palette.ink2,
														}}>
														{tag}
													</Box>
												))}
											</Box>
											<Typography sx={{ fontSize: 11, fontWeight: 600, color: palette.ink, mb: 0.75 }}>Remark</Typography>
											<Box
												component="textarea"
												value={remarkText}
												onChange={(e) => setRemarkText(e.target.value)}
												placeholder="Write your remark here..."
												sx={{
													width: "100%",
													minHeight: 110,
													border: `1px solid ${palette.border}`,
													borderRadius: 1.5,
													p: 1.25,
													fontSize: 12,
													fontFamily: "Roboto, sans-serif",
													color: palette.ink,
													resize: "vertical",
													outline: "none",
													mb: 1.5,
												}}
											/>
											<Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
												<Box
													component="button"
													onClick={closeRemarkModal}
													sx={{
														fontSize: 11,
														px: 1.25,
														py: 0.75,
														borderRadius: 1,
														border: `1px solid ${palette.border2}`,
														bgcolor: "#fff",
														color: palette.ink2,
														cursor: "pointer",
													}}>
													Cancel
												</Box>
												<Box
													component="button"
													onClick={submitRemark}
													sx={{
														fontSize: 11,
														px: 1.25,
														py: 0.75,
														borderRadius: 1,
														border: "none",
														bgcolor: palette.ink,
														color: "#fff",
														cursor: "pointer",
													}}>
													Save remark
												</Box>
											</Box>
										</Box>
									</Box>
								)}

								<Box
									sx={{
										display: "flex",
										alignItems: "center",
										justifyContent: "space-between",
										gap: 1.5,
										flexWrap: "wrap",
										px: 1.5,
										py: 1.1,
										bgcolor: selectedRows.size > 0 ? palette.surface2 : "#fff",
										border: `1px solid ${palette.border}`,
										borderRadius: 1.5,
									}}>
									<Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
										<Typography sx={{ fontSize: 12, color: palette.ink3 }}>
											{selectedRows.size > 0 ? `${selectedRows.size} selected` : `${filteredTableRows.length} styles`}
										</Typography>
										<Box sx={{ display: selectedRows.size > 0 ? "flex" : "none", alignItems: "center", gap: 0.75 }}>
											<Box sx={{ width: 1, height: 18, bgcolor: palette.border }} />
											<Box
												component="button"
												onClick={openRemarkModal}
												sx={{
													display: "inline-flex",
													alignItems: "center",
													gap: 0.6,
													fontSize: 11,
													px: 1.15,
													py: 0.7,
													borderRadius: 1,
													border: "none",
													bgcolor: palette.ink,
													color: "#fff",
													cursor: "pointer",
												}}>
												<DescriptionRounded sx={{ fontSize: 15 }} />
												Add remark
											</Box>
											<Box
												component="button"
												onClick={clearSelection}
												sx={{
													display: "inline-flex",
													alignItems: "center",
													gap: 0.5,
													fontSize: 11,
													px: 1.15,
													py: 0.7,
													borderRadius: 1,
													border: `1px solid ${palette.border2}`,
													bgcolor: "#fff",
													color: palette.ink2,
													cursor: "pointer",
												}}>
												<CloseRounded sx={{ fontSize: 15 }} />
												Deselect
											</Box>
										</Box>
									</Box>

									<Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
										{[
											{ label: "Columns", icon: <Inventory2Rounded sx={{ fontSize: 15 }} /> },
											{ label: "Export", icon: <DescriptionRounded sx={{ fontSize: 15 }} /> },
										].map((btn) => (
											<Box
												key={btn.label}
												component="button"
												sx={{
													display: "inline-flex",
													alignItems: "center",
													gap: 0.6,
													fontSize: 11,
													px: 1.15,
													py: 0.7,
													borderRadius: 1,
													border: `1px solid ${palette.border2}`,
													bgcolor: "#fff",
													color: palette.ink2,
													cursor: "pointer",
												}}>
												{btn.icon}
												{btn.label}
											</Box>
										))}
										<Box
											component="button"
											onClick={() => setShowImages((prev) => !prev)}
											aria-checked={showImages}
											role="switch"
											sx={{
												display: "inline-flex",
												alignItems: "center",
												gap: 0.75,
												fontSize: 11,
												px: 1.15,
												py: 0.7,
												borderRadius: 1,
												border: `1px solid ${palette.border2}`,
												bgcolor: "#fff",
												color: palette.ink2,
												cursor: "pointer",
											}}>
											Images
											<Box
												sx={{
													width: 36,
													height: 20,
													borderRadius: 999,
													bgcolor: showImages ? palette.ink : palette.surface3,
													position: "relative",
													transition: "background-color 150ms ease",
													flexShrink: 0,
												}}>
												<Box
													sx={{
														position: "absolute",
														top: 2,
														left: showImages ? 18 : 2,
														width: 16,
														height: 16,
														borderRadius: "50%",
														bgcolor: "#fff",
														boxShadow: "0 1px 2px rgba(0,0,0,0.2)",
														transition: "left 150ms ease",
													}}
												/>
											</Box>
										</Box>
									</Box>
								</Box>

								<Box sx={{ overflowX: "auto" }}>
									<Box sx={{ minWidth: showImages ? 1480 : 1420 }}>
										<Box
											sx={{
												display: "grid",
												gridTemplateColumns: tableGridTemplate,
												gap: 0.75,
												pb: 1,
												fontSize: 10,
												color: palette.ink4,
												borderBottom: `1px solid ${palette.border}`,
											}}>
											<Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
												<Box
													component="input"
													type="checkbox"
													checked={allCurrentSelected}
													onChange={(e) => toggleAllRows(e.target.checked)}
													aria-label="Select all rows"
													sx={{ width: 16, height: 16, accentColor: palette.ink }}
												/>
											</Box>
											<Box
												component="button"
												onClick={() => sortBy("status")}
												sx={{ border: "none", bgcolor: "transparent", p: 0, textAlign: "left", color: palette.ink4 }}>
												Status {sortCol === "status" ? (sortDir === 1 ? "↑" : "↓") : "⇅"}
											</Box>
											{showImages ? <Box>Image</Box> : null}
											<Box
												component="button"
												onClick={() => sortBy("id")}
												sx={{ border: "none", bgcolor: "transparent", p: 0, textAlign: "left", color: palette.ink4 }}>
												Style ID {sortCol === "id" ? (sortDir === 1 ? "↑" : "↓") : "⇅"}
											</Box>
											<Box
												component="button"
												onClick={() => sortBy("name")}
												sx={{ border: "none", bgcolor: "transparent", p: 0, textAlign: "left", color: palette.ink4 }}>
												Name {sortCol === "name" ? (sortDir === 1 ? "↑" : "↓") : "⇅"}
											</Box>
											<Box
												component="button"
												onClick={() => sortBy("season")}
												sx={{ border: "none", bgcolor: "transparent", p: 0, textAlign: "left", color: palette.ink4 }}>
												Season {sortCol === "season" ? (sortDir === 1 ? "↑" : "↓") : "⇅"}
											</Box>
											<Box>Order</Box>
											<Box
												component="button"
												onClick={() => sortBy("brand")}
												sx={{ border: "none", bgcolor: "transparent", p: 0, textAlign: "left", color: palette.ink4 }}>
												Brand {sortCol === "brand" ? (sortDir === 1 ? "↑" : "↓") : "⇅"}
											</Box>
											<Box>Sub brand</Box>
											<Box>Division</Box>
											<Box>Gender</Box>
											<Box
												component="button"
												onClick={() => sortBy("category")}
												sx={{ border: "none", bgcolor: "transparent", p: 0, textAlign: "left", color: palette.ink4 }}>
												Category {sortCol === "category" ? (sortDir === 1 ? "↑" : "↓") : "⇅"}
											</Box>
											<Box>Sub categ.</Box>
											<Box>Colors</Box>
											<Box>Created by</Box>
										</Box>

										<Stack spacing={0}>
											{visibleTableRows.length ? (
												visibleTableRows.map((row, index) => {
													const selected = selectedRows.has(row.id);
													const statusColor = row.status === "Published" ? palette.green : palette.red;
													const statusBg = row.status === "Published" ? palette.greenBg : palette.redBg;
													return (
														<Box
															key={row.id}
															sx={{
																display: "grid",
																gridTemplateColumns: tableGridTemplate,
																gap: 0.75,
																alignItems: "center",
																py: 1,
																borderBottom: `1px solid ${palette.surface2}`,
																bgcolor: selected ? palette.surface2 : index % 2 === 0 ? "#fff" : palette.surface,
															}}>
															<Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
																<Box
																	component="input"
																	type="checkbox"
																	checked={selected}
																	onChange={() => toggleRow(row.id)}
																	aria-label={`Select ${row.id}`}
																	sx={{ width: 16, height: 16, accentColor: palette.ink }}
																/>
															</Box>
															<Box
																sx={{
																	fontSize: 10,
																	px: 0.9,
																	py: 0.4,
																	borderRadius: 999,
																	display: "inline-flex",
																	alignItems: "center",
																	justifyContent: "center",
																	bgcolor: statusBg,
																	color: statusColor,
																	width: "fit-content",
																	gap: 0.5,
																}}>
																{row.status}
															</Box>
															{showImages ? (
																<Box
																	sx={{
																		width: 28,
																		height: 28,
																		borderRadius: 1,
																		bgcolor: palette.surface2,
																		border: `1px solid ${palette.border}`,
																		display: "grid",
																		placeItems: "center",
																		color: palette.ink4,
																		fontSize: 12,
																	}}>
																	<Inventory2Rounded sx={{ fontSize: 16 }} />
																</Box>
															) : null}
															<Typography sx={{ fontSize: 11, color: palette.ink2, fontWeight: 600 }}>{row.id}</Typography>
															<Typography sx={{ fontSize: 11, color: palette.ink }}>{row.name}</Typography>
															<Typography sx={{ fontSize: 11, color: palette.ink2 }}>{row.season}</Typography>
															<Typography sx={{ fontSize: 11, color: palette.ink2 }}>{row.order}</Typography>
															<Typography sx={{ fontSize: 11, color: palette.ink2 }}>{row.brand}</Typography>
															<Typography sx={{ fontSize: 11, color: palette.ink2 }}>{row.sub}</Typography>
															<Typography sx={{ fontSize: 11, color: palette.ink2 }}>{row.div}</Typography>
															<Typography sx={{ fontSize: 11, color: palette.ink2 }}>{row.gender}</Typography>
															<Typography sx={{ fontSize: 11, color: palette.ink2 }}>{row.category}</Typography>
															<Typography sx={{ fontSize: 11, color: palette.ink2 }}>{row.subcat}</Typography>
															<Typography sx={{ fontSize: 11, color: palette.ink2, textAlign: "center" }}>{row.colors}</Typography>
															<Typography sx={{ fontSize: 11, color: palette.ink2 }}>{row.by}</Typography>
														</Box>
													);
												})
											) : (
												<Box sx={{ py: 4, textAlign: "center", color: palette.ink3, fontSize: 12 }}>
													No styles match your filters
												</Box>
											)}
										</Stack>
									</Box>
								</Box>

								<Box
									sx={{
										display: "flex",
										alignItems: "center",
										justifyContent: "space-between",
										gap: 1.5,
										flexWrap: "wrap",
										pt: 1.25,
										mt: 1.25,
										borderTop: `1px solid ${palette.border}`,
										fontSize: 10,
										color: palette.ink3,
									}}>
									<Box>
										{filteredTableRows.length
											? `Showing ${tableStart + 1}–${Math.min(tableStart + tablePageSize, filteredTableRows.length)} of ${filteredTableRows.length}`
											: "No results"}
									</Box>
									{filteredTableRows.length ? (
										<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
											<Box
												component="button"
												onClick={() => setTablePage((prev) => Math.max(1, prev - 1))}
												disabled={tablePage === 1}
												sx={{
													width: 28,
													height: 28,
													borderRadius: 1,
													border: `1px solid ${palette.border2}`,
													bgcolor: "#fff",
													color: palette.ink2,
													cursor: tablePage === 1 ? "not-allowed" : "pointer",
													display: "grid",
													placeItems: "center",
												}}>
												<ChevronLeftRounded sx={{ fontSize: 16 }} />
											</Box>
											{Array.from({ length: tablePageCount }, (_, i) => i + 1).map((p) => (
												<Box
													key={p}
													component="button"
													onClick={() => setTablePage(p)}
													sx={{
														minWidth: 28,
														height: 28,
														borderRadius: 1,
														border: `1px solid ${p === tablePage ? palette.ink : palette.border2}`,
														bgcolor: p === tablePage ? palette.ink : "#fff",
														color: p === tablePage ? "#fff" : palette.ink2,
														cursor: "pointer",
														fontSize: 11,
													}}>
													{p}
												</Box>
											))}
											<Box
												component="button"
												onClick={() => setTablePage((prev) => Math.min(tablePageCount, prev + 1))}
												disabled={tablePage === tablePageCount}
												sx={{
													width: 28,
													height: 28,
													borderRadius: 1,
													border: `1px solid ${palette.border2}`,
													bgcolor: "#fff",
													color: palette.ink2,
													cursor: tablePage === tablePageCount ? "not-allowed" : "pointer",
													display: "grid",
													placeItems: "center",
												}}>
												<ChevronRightRounded sx={{ fontSize: 16 }} />
											</Box>
										</Box>
									) : null}
								</Box>
							</Box>
						</Box>

						<Box
							sx={{
								display: "none",
								gridTemplateColumns: { xs: "1fr", md: "repeat(5, 1fr)" },
								gap: 1.25,
							}}>
							<MetricCard label="Total styles" value={metrics.total} delta="↑ 42 this week" deltaColor={palette.green} />
							<MetricCard label="Published" value={metrics.pub} delta="66% of total" deltaColor={palette.green} />
							<MetricCard label="In workflow" value={metrics.wf} delta="⏳ 24 overdue" deltaColor={palette.amber} />
							<MetricCard
								label="Draft / design"
								value={metrics.draft}
								delta="Awaiting fabric"
								deltaColor={palette.ink3}
							/>
							<MetricCard
								label="Completeness"
								value={
									<>
										{metrics.comp}
										<span style={{ fontSize: 16, fontFamily: "Roboto, sans-serif" }}>%</span>
									</>
								}
								delta="↑ 3% vs last month"
								deltaColor={palette.green}
							/>
						</Box>

						<Box sx={{ display: "none", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1.75 }}>
							<Box sx={cardSx()}>
								<SectionTitle title="Workflow pipeline" sub="styles in progress" />
								{stageRows.map((row) => (
									<BarRow
										key={row.label}
										label={row.label}
										value={row.value}
										pct={row.pct}
										color={row.color}
										valueWidth={34}
										pctWidth={30}
									/>
								))}
								<Stack direction="row" spacing={1.5} sx={{ mt: 2, flexWrap: "wrap" }}>
									{[
										["Design", "#93c5fd"],
										["Fabric", "#6366f1"],
										["Trims", "#8B5CF6"],
										["Review", "#a78bfa"],
										["Published", "#059669"],
									].map(([label, color]) => (
										<Chip
											key={label}
											label={label}
											size="small"
											sx={{
												height: 22,
												fontSize: 10,
												bgcolor: "transparent",
												"& .MuiChip-label": { px: 0.75 },
												"&::before": {
													content: '""',
													width: 9,
													height: 9,
													bgcolor: color,
													borderRadius: 0.5,
													mr: 0.75,
												},
											}}
										/>
									))}
								</Stack>
							</Box>

							<Box sx={cardSx()}>
								<SectionTitle title="Styles by brand" />
								{[
									["Rare Rabbit", "412", 82, palette.rr],
									["Rareism", "341", 68, palette.rm],
									["Rareones", "218", 43, palette.ro],
									["Rarez", "313", 63, palette.rz],
								].map(([label, value, pct, color]) => (
									<BarRow
										key={label}
										label={label}
										value={value}
										pct={pct}
										color={color}
										valueWidth={34}
										pctWidth={34}
										labelWidth={100}
									/>
								))}

								{/* <Box sx={{ mt: 2, pt: 1.5, borderTop: `1px solid ${palette.border}` }}>
									<Typography sx={{ fontSize: 11, fontWeight: 600, color: palette.ink, mb: 1 }}>
										Style color assignment
									</Typography>
									<Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
										<Box
											sx={{
												flex: 1,
												height: 8,
												bgcolor: palette.surface2,
												borderRadius: 999,
												overflow: "hidden",
											}}>
											<Box sx={{ width: "61%", height: "100%", bgcolor: "#6366f1", borderRadius: 999 }} />
										</Box>
										<Typography sx={{ fontSize: 11, color: palette.ink3 }}>61% assigned</Typography>
									</Box>
									<Typography sx={{ fontSize: 10, color: palette.ink4, mt: 0.75 }}>
										499 styles have no color (optional field)
									</Typography>
								</Box> */}
							</Box>
						</Box>

						<Box sx={{ display: "none", gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" }, gap: 1.75 }}>
							<Box sx={cardSx()}>
								<SectionTitle title="Data completeness" />
								{completenessRows.map((row) => (
									<BarRow
										key={row.label}
										label={row.label}
										value=""
										pct={row.pct}
										color={row.color}
										labelWidth={110}
										valueWidth={0}
										pctWidth={34}
									/>
								))}
								<Box
									sx={{
										mt: 1.5,
										pt: 1.25,
										borderTop: `1px solid ${palette.border}`,
										display: "flex",
										alignItems: "center",
										gap: 1.5,
									}}>
									<Typography sx={{ fontFamily: "Roboto, sans-serif", fontSize: 24, fontWeight: 700 }}>
										78%
									</Typography>
									<Typography sx={{ fontSize: 10, color: palette.ink3 }}>
										Overall avg
										<br />
										<Box component="span" sx={{ color: palette.amber }}>
											Trims &amp; images need work
										</Box>
									</Typography>
								</Box>
							</Box>
							{/* 
							<Box sx={cardSx()}>
								<SectionTitle title="Stale styles" sub="24 overdue" />
								{staleRows.map((row) => (
									<Box
										key={row.code}
										sx={{
											display: "flex",
											alignItems: "center",
											gap: 1,
											mb: 0.6,
											p: 1,
											bgcolor: palette.surface2,
											borderRadius: 1,
										}}>
										<Typography
											sx={{
												fontSize: 11,
												fontFamily: "Roboto, sans-serif",
												fontWeight: 500,
												color: palette.ink,
												width: 72,
											}}>
											{row.code}
										</Typography>
										<Typography sx={{ fontSize: 11, color: palette.ink2, flex: 1 }}>{row.name}</Typography>
										<Box
											sx={{
												fontSize: 10,
												px: 0.9,
												py: 0.25,
												borderRadius: 999,
												bgcolor:
													row.className === "ss-fab"
														? "#dbeafe"
														: row.className === "ss-tri"
															? "#ede9fe"
															: "#fef3c7",
												color:
													row.className === "ss-fab"
														? "#1e40af"
														: row.className === "ss-tri"
															? "#5b21b6"
															: "#92400e",
											}}>
											{row.stage}
										</Box>
										<Typography
											sx={{
												fontSize: 11,
												fontFamily: "Roboto, sans-serif",
												fontWeight: 500,
												color: palette.red,
												width: 30,
												textAlign: "right",
											}}>
											{row.days}d
										</Typography>
									</Box>
								))}
							</Box> */}

							{/* <Box sx={cardSx()}>
								<SectionTitle title="Recent activity" />
								{activityRows.map((row) => (
									<Box
										key={row.title}
										sx={{
											display: "flex",
											alignItems: "flex-start",
											gap: 1.5,
											py: 1,
											borderBottom: `1px solid ${palette.border}`,
										}}>
										<Box
											sx={{
												width: 28,
												height: 28,
												borderRadius: "50%",
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
												bgcolor: row.bg,
												color: row.fg,
												flexShrink: 0,
											}}>
											{row.icon}
										</Box>
										<Box sx={{ flex: 1 }}>
											<Typography sx={{ fontSize: 12, color: palette.ink }}>{row.title}</Typography>
											<Typography sx={{ fontSize: 10, color: palette.ink4, mt: 0.25 }}>{row.sub}</Typography>
										</Box>
										<Typography sx={{ fontSize: 10, color: palette.ink4, flexShrink: 0 }}>{row.time}</Typography>
									</Box>
								))}
							</Box> */}
						</Box>
					</Stack>
				)}

				{activePage === "categories" && (
					<Stack spacing={2.5}>
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								justifyContent: "space-between",
								gap: 2,
								flexWrap: "wrap",
							}}>
							<Box>
								<Typography sx={{ fontFamily: "Roboto, sans-serif", fontSize: 26, fontWeight: 700, lineHeight: 1.1 }}>
									Category Analysis
								</Typography>
								<Typography sx={{ fontSize: 12, color: palette.ink3, mt: 0.5 }}>
									Style counts, publish rates and brand split by category
								</Typography>
							</Box>
							<PageNav activePage={activePage} setActivePage={setActivePage} />
						</Box>

						<Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(4, 1fr)" }, gap: 1.25 }}>
							<MetricCard label="Total styles" value={metrics.total} delta="All categories" />
							<MetricCard label="Categories" value="6" delta="Active types" />
							<MetricCard label="Highest publish" value="76%" delta="Belts category" />
							<MetricCard label="Needs attention" value="2" delta="Accessories, Trolley" />
						</Box>

						<Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1.75 }}>
							<Box sx={cardSx()}>
								<SectionTitle title="Category-wise style count" sub={brandMeta[activeBrand].label.toLowerCase()} />
								<Box
									sx={{
										display: "grid",
										gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
										pb: 1,
										fontSize: 10,
										color: palette.ink4,
										borderBottom: `1px solid ${palette.border}`,
									}}>
									<Box>Category</Box>
									<Box sx={{ textAlign: "right" }}>Total</Box>
									<Box sx={{ textAlign: "right" }}>Published</Box>
									<Box sx={{ textAlign: "right" }}>In workflow</Box>
									<Box sx={{ textAlign: "right" }}>Progress</Box>
								</Box>
								<Stack spacing={0}>
									{filteredCatRows.map((row) => {
										const width = row.total
											? Math.round((row.total / Math.max(...filteredCatRows.map((r) => r.total))) * 100)
											: 0;
										return (
											<Box
												key={row.cat}
												sx={{
													display: "grid",
													gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
													py: 1,
													borderBottom: `1px solid ${palette.border}`,
												}}>
												<Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
													<Box
														sx={{ width: 7, height: 7, borderRadius: 0.5, bgcolor: row.color, mt: 0.25 }}
													/>
													<Typography sx={{ fontSize: 12, color: palette.ink }}>{row.cat}</Typography>
												</Box>
												<Typography sx={{ fontSize: 12, color: palette.ink3, textAlign: "right" }}>
													{row.total}
												</Typography>
												<Typography sx={{ fontSize: 12, color: palette.ink3, textAlign: "right" }}>
													{row.pub || "—"}
												</Typography>
												<Typography sx={{ fontSize: 12, color: palette.ink3, textAlign: "right" }}>
													{row.wf}
												</Typography>
												<Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
													<Box
														sx={{
															width: 60,
															height: 4,
															bgcolor: palette.surface2,
															borderRadius: 999,
															overflow: "hidden",
														}}>
														<Box
															sx={{
																width: `${width}%`,
																height: "100%",
																bgcolor: row.color,
																borderRadius: 999,
															}}
														/>
													</Box>
												</Box>
											</Box>
										);
									})}
								</Stack>
							</Box>

							<Box sx={cardSx()}>
								<SectionTitle title="Published vs total" />
								<Box sx={{ fontSize: 11, color: palette.ink4, mb: 1 }}>By category and brand</Box>
								<Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 3 }}>
									<Box>
										<Box sx={{ fontSize: 11, color: palette.ink4, mb: 1 }}>By category</Box>
										{[
											["Apparel", 72, "429 / 596", "#059669"],
											["Footwear", 66, "206 / 313", "#059669"],
											["Bags", 61, "90 / 148", "#d97706"],
											["Belts", 76, "70 / 92", "#059669"],
											["Trolley", 55, "37 / 67", "#d97706"],
											["Accessories", 51, "35 / 68", "#dc2626"],
										].map(([label, pct, value, color]) => (
											<BarRow
												key={label}
												label={label}
												value={value}
												pct={pct}
												color={color}
												valueWidth={72}
												pctWidth={0}
												labelWidth={80}
											/>
										))}
									</Box>
									<Box>
										<Box sx={{ fontSize: 11, color: palette.ink4, mb: 1 }}>By brand</Box>
										{[
											["Rare Rabbit", 72, "298 / 412", palette.rr],
											["Rareism", 63, "214 / 341", palette.rm],
											["Rareones", 59, "129 / 218", palette.ro],
											["Rarez", 66, "206 / 313", palette.rz],
										].map(([label, pct, value, color]) => (
											<BarRow
												key={label}
												label={label}
												value={value}
												pct={pct}
												color={color}
												valueWidth={72}
												pctWidth={0}
												labelWidth={80}
											/>
										))}
									</Box>
								</Box>
							</Box>
						</Box>
					</Stack>
				)}

				{activePage === "colors" && (
					<Stack spacing={2.5}>
						<PageNav activePage={activePage} setActivePage={setActivePage} />
						<Box>
							<Typography sx={{ fontFamily: "Roboto, sans-serif", fontSize: 26, fontWeight: 700, lineHeight: 1.1 }}>
								Color Report
							</Typography>
							<Typography sx={{ fontSize: 12, color: palette.ink3, mt: 0.5 }}>
								38 unique colors across 785 assigned styles
							</Typography>
						</Box>

						<Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(4, 1fr)" }, gap: 1.25 }}>
							<MetricCard label="Unique colors" value="38" delta="Named colors in use" />
							<MetricCard label="Assigned styles" value="785" delta="61% of total" />
							<MetricCard label="No color" value="499" delta="Optional field" />
							<MetricCard label="Top color" value="Black" delta="187 styles" />
						</Box>

						<Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1.75 }}>
							<Box sx={cardSx()}>
								<SectionTitle title="Top 12 colors by style count" />
								<Box sx={{ display: "flex", flexDirection: "column", gap: 1.1 }}>
									{[
										["Black", 187, "#1a1a1a", 100],
										["White", 164, "#e8e8e4", 88],
										["Navy Blue", 141, "#1e3a5f", 76],
										["Cobalt Blue", 55, "#1e5ba8", 30],
										["Olive", 118, "#6b7c4a", 63],
										["Forest Green", 51, "#2d5c3a", 27],
										["Slate Grey", 76, "#7d8fa0", 41],
										["Blush Pink", 68, "#e8b4c0", 36],
										["Camel", 102, "#c19a6b", 55],
										["Burgundy", 89, "#7b2d42", 48],
										["Mustard", 62, "#f0c070", 33],
										["Cream", 48, "#f5f0e8", 26],
									].map(([label, value, color, pct]) => (
										<BarRow
											key={label}
											label={label}
											value={value}
											pct={pct}
											color={color}
											valueWidth={34}
											pctWidth={0}
											labelWidth={110}
										/>
									))}
								</Box>
							</Box>

							<Box sx={cardSx()}>
								<SectionTitle title="Color family breakdown" />
								<Box
									sx={{
										display: "grid",
										gridTemplateColumns: "repeat(auto-fill, minmax(80px, 1fr))",
										gap: 1,
									}}>
									{colorTiles.map((tile) => (
										<Box
											key={tile.name}
											sx={{ border: `1px solid ${palette.border}`, borderRadius: 1, p: 1, cursor: "pointer" }}>
											<Box
												sx={{
													width: 26,
													height: 26,
													borderRadius: 1,
													border: tile.border ? "1px solid #ddd" : `1px solid ${palette.border}`,
													bgcolor: tile.swatch,
													mb: 0.75,
												}}
											/>
											<Typography sx={{ fontSize: 10, color: palette.ink2, fontWeight: 600 }}>
												{tile.name}
											</Typography>
											<Typography
												sx={{
													fontSize: 13,
													fontFamily: "Roboto, sans-serif",
													fontWeight: 700,
													color: palette.ink,
												}}>
												{tile.cnt}
											</Typography>
											<Typography sx={{ fontSize: 10, color: palette.ink4 }}>{tile.pct}</Typography>
										</Box>
									))}
								</Box>
							</Box>
						</Box>
					</Stack>
				)}

				{activePage === "timeline" && (
					<Stack spacing={2.5}>
						<PageNav activePage={activePage} setActivePage={setActivePage} />
						<Box>
							<Typography sx={{ fontFamily: "Roboto, sans-serif", fontSize: 26, fontWeight: 700, lineHeight: 1.1 }}>
								Style Timeline
							</Typography>
							<Typography sx={{ fontSize: 12, color: palette.ink3, mt: 0.5 }}>
								Track every style through design → fabric → trims → publish
							</Typography>
						</Box>

						<Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
							{stageFilters.map((filter) => (
								<Chip
									key={filter.key}
									label={filter.label}
									onClick={() => setTimelineFilter(filter.key)}
									size="small"
									sx={{
										height: 26,
										fontSize: 10,
										bgcolor: timelineFilter === filter.key ? palette.ink : "#fff",
										color: timelineFilter === filter.key ? "#fff" : palette.ink3,
										border: `1px solid ${timelineFilter === filter.key ? palette.ink : palette.border2}`,
										"&:hover": {
											bgcolor: timelineFilter === filter.key ? palette.ink : palette.surface2,
										},
									}}
								/>
							))}
						</Box>

						<Box
							sx={{
								display: "grid",
								gridTemplateColumns: { xs: "1fr", lg: "1fr 280px" },
								gap: 1.75,
								alignItems: "start",
							}}>
							<Box sx={{ position: "relative", pl: 3 }}>
								<Box
									sx={{
										position: "absolute",
										left: 6,
										top: 0,
										bottom: 0,
										width: 2,
										bgcolor: palette.surface3,
										borderRadius: 1,
									}}
								/>
								<Stack spacing={1.2}>
									{filteredTimeline.map((row) => (
										<Box key={row.code} sx={{ position: "relative" }}>
											<Box
												sx={{
													position: "absolute",
													left: -24,
													top: 16,
													width: 14,
													height: 14,
													borderRadius: "50%",
													bgcolor: row.overdue
														? "#fca5a5"
														: row.stage === "published"
															? "#059669"
															: row.stage === "fabric"
																? "#3b82f6"
																: row.stage === "trims"
																	? "#8B5CF6"
																	: "#a78bfa",
													boxShadow: "0 0 0 2px #fff",
												}}
											/>
											<Box sx={{ ...cardSx(), borderColor: row.overdue ? "#fca5a5" : palette.border }}>
												<Box sx={{ display: "flex", justifyContent: "space-between", gap: 1.5 }}>
													<Box>
														<Box
															sx={{
																display: "flex",
																alignItems: "center",
																gap: 1,
																flexWrap: "wrap",
																mb: 0.5,
															}}>
															<Typography
																sx={{
																	fontSize: 11,
																	fontFamily: "Roboto, sans-serif",
																	fontWeight: 500,
																	color: palette.ink3,
																}}>
																{row.code}
															</Typography>
															<Box
																sx={{
																	fontSize: 10,
																	px: 1,
																	py: 0.3,
																	borderRadius: 999,
																	bgcolor:
																		row.stage === "published"
																			? "#dcfce7"
																			: row.stage === "fabric"
																				? "#e0f2fe"
																				: row.stage === "trims"
																					? "#ede9fe"
																					: "#fef3c7",
																	color:
																		row.stage === "published"
																			? "#14532d"
																			: row.stage === "fabric"
																				? "#075985"
																				: row.stage === "trims"
																					? "#4c1d95"
																					: "#78350f",
																}}>
																{row.stage}
															</Box>
															{row.overdue ? (
																<Box
																	sx={{
																		fontSize: 10,
																		px: 1,
																		py: 0.3,
																		borderRadius: 999,
																		bgcolor: palette.redBg,
																		color: palette.red,
																	}}>
																	{row.days}d overdue
																</Box>
															) : null}
														</Box>
														<Typography sx={{ fontSize: 13, fontWeight: 600, color: palette.ink }}>
															{row.name}
														</Typography>
														<Typography
															sx={{
																fontSize: 10,
																color: palette.ink4,
																display: "flex",
																gap: 1,
																flexWrap: "wrap",
																mt: 0.5,
															}}>
															<Box component="span">Brand: {row.brand}</Box>
															<Box component="span">Category: {row.cat}</Box>
															<Box component="span">Assignee: {row.assignee}</Box>
															<Box component="span">Color: {row.color}</Box>
														</Typography>
													</Box>
													<Typography sx={{ fontSize: 10, color: palette.ink4, flexShrink: 0 }}>
														{row.ts}
													</Typography>
												</Box>

												<Box sx={{ display: "flex", gap: 0.4, mt: 1.25 }}>
													{Array.from({ length: 5 }).map((_, idx) => (
														<Box
															key={idx}
															sx={{
																flex: 1,
																height: 4,
																borderRadius: 999,
																bgcolor:
																	idx < row.done - 1
																		? "#2a7a4f"
																		: idx === row.done - 1
																			? row.stage === "published"
																				? "#2a7a4f"
																				: "#c8a96e"
																			: palette.surface3,
															}}
														/>
													))}
												</Box>
											</Box>
										</Box>
									))}
								</Stack>
							</Box>

							<Stack spacing={1.5} sx={{ position: { lg: "sticky" }, top: 20 }}>
								<Box sx={cardSx()}>
									<SectionTitle title="Stage summary" />
									{[
										["Design", 125, 100, "#93c5fd"],
										["Fabric", 93, 74, "#6366f1"],
										["Trims", 64, 51, "#8B5CF6"],
										["Review", 41, 33, "#a78bfa"],
										["Published", 847, 66, "#059669"],
									].map(([label, value, pct, color]) => (
										<BarRow
											key={label}
											label={label}
											value={value}
											pct={pct}
											color={color}
											valueWidth={34}
											pctWidth={0}
											labelWidth={90}
										/>
									))}
								</Box>
								<Box sx={cardSx()}>
									<SectionTitle title="Avg days per stage" />
									{[
										["Design → Fabric", "4.2d", 40],
										["Fabric → Trims", "5.8d", 55],
										["Trims → Review", "3.1d", 30],
										["Review → Pub", "2.1d", 20],
									].map(([label, value, pct]) => (
										<BarRow
											key={label}
											label={label}
											value={value}
											pct={pct}
											color="#d97706"
											valueWidth={34}
											pctWidth={0}
											labelWidth={105}
										/>
									))}
								</Box>
							</Stack>
						</Box>
					</Stack>
				)}

				{activePage === "techview" && (
					<Stack spacing={2.5}>
						<PageNav activePage={activePage} setActivePage={setActivePage} />
						<Box>
							<Typography sx={{ fontFamily: "Roboto, sans-serif", fontSize: 26, fontWeight: 700, lineHeight: 1.1 }}>
								Technical Drawing
							</Typography>
							<Typography sx={{ fontSize: 12, color: palette.ink3, mt: 0.5 }}>
								Category-wise technical spec view with design, fabric and trims annotations
							</Typography>
						</Box>

						<Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
							{techTabs.map((tab) => (
								<Box
									component="button"
									key={tab.key}
									onClick={() => setActiveTech(tab.key)}
									sx={{
										fontSize: 11,
										px: 1.5,
										py: 0.75,
										borderRadius: 1,
										textTransform: "none",
										border: `1px solid ${palette.border2}`,
										bgcolor: activeTech === tab.key ? palette.ink : "#fff",
										color: activeTech === tab.key ? "#fff" : palette.ink3,
										display: "flex",
										alignItems: "center",
										gap: 0.75,
										cursor: "pointer",
									}}>
									<Box sx={{ display: "inline-flex", alignItems: "center", fontSize: 16 }}>{tab.icon}</Box>
									{tab.label}
								</Box>
							))}
						</Box>

						<Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 380px" }, gap: 1.75 }}>
							<Box sx={{ ...cardSx(), p: 2.5 }}>
								<Typography
									sx={{
										fontSize: 10,
										color: palette.ink4,
										mb: 1.5,
										letterSpacing: "0.06em",
										textTransform: "uppercase",
									}}>
									Style: {techMeta[activeTech].code}
								</Typography>
								<TechDrawing active={activeTech} />
								<Box
									sx={{
										display: "flex",
										gap: 1.25,
										flexWrap: "wrap",
										mt: 2,
										pt: 1.75,
										borderTop: `1px solid ${palette.border}`,
									}}>
									{[
										["Design pattern", "#c8a96e"],
										["Fabric detail", "#3b82f6"],
										["Trims detail", "#8b5cf6"],
										["Construction", "#10b981"],
									].map(([label, color]) => (
										<Box
											key={label}
											sx={{
												fontSize: 10,
												color: palette.ink3,
												display: "flex",
												alignItems: "center",
												gap: 0.6,
											}}>
											<Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: color }} />
											{label}
										</Box>
									))}
								</Box>
							</Box>

							<Stack spacing={1.5}>
								<Box sx={{ ...cardSx(), overflow: "hidden", p: 0 }}>
									<Box sx={{ bgcolor: palette.ink, color: "#fff", p: 1.5 }}>
										<Typography
											sx={{
												fontSize: 9,
												letterSpacing: "0.08em",
												textTransform: "uppercase",
												color: "rgba(255,255,255,0.5)",
											}}>
											Technical annotation
										</Typography>
										<Typography sx={{ fontFamily: "Roboto, sans-serif", fontSize: 16, fontWeight: 700 }}>
											Select a callout point
										</Typography>
									</Box>
									<Box sx={{ p: 1.75 }}>
										<Typography sx={{ fontSize: 12, color: palette.ink4 }}>
											Click any highlighted dot on the drawing to see details.
										</Typography>
									</Box>
								</Box>

								<Box sx={cardSx()}>
									<SectionTitle title="Callout index" />
									<Stack spacing={0.75}>
										{[
											["Design pattern", "Collar"],
											["Fabric detail", "Body"],
											["Trims detail", "Placket"],
											["Construction", "Hem"],
										].map(([type, name]) => (
											<Box
												key={name}
												sx={{
													display: "flex",
													alignItems: "center",
													gap: 1.25,
													p: 1.25,
													borderRadius: 1,
													border: `1px solid ${palette.border}`,
													bgcolor: "#fff",
												}}>
												<Box
													sx={{
														width: 10,
														height: 10,
														borderRadius: "50%",
														bgcolor:
															type === "Design pattern"
																? "#c8a96e"
																: type === "Fabric detail"
																	? "#3b82f6"
																	: type === "Trims detail"
																		? "#8b5cf6"
																		: "#10b981",
													}}
												/>
												<Box>
													<Typography
														sx={{
															fontSize: 9,
															color: palette.ink4,
															textTransform: "uppercase",
															letterSpacing: "0.07em",
														}}>
														{type}
													</Typography>
													<Typography sx={{ fontSize: 12, color: palette.ink }}>{name}</Typography>
												</Box>
												<Typography sx={{ ml: "auto", color: palette.ink4 }}>→</Typography>
											</Box>
										))}
									</Stack>
								</Box>

								<Box sx={cardSx()}>
									<SectionTitle title="Style info" />
									<Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
										{[
											["Code", techMeta[activeTech].code.split("·")[0].trim()],
											["Category", techMeta[activeTech].title],
											[
												"Brand",
												activeTech === "shoe"
													? "Rarez"
													: activeTech === "bag"
														? "Rareones"
														: activeTech === "belt"
															? "Rare Rabbit"
															: "Rareism",
											],
											["Season", "SS25"],
										].map(([label, value], idx, arr) => (
											<Box
												key={label}
												sx={{
													display: "flex",
													justifyContent: "space-between",
													py: 0.5,
													borderBottom: idx === arr.length - 1 ? "none" : `1px solid ${palette.border}`,
													fontSize: 11,
												}}>
												<Typography sx={{ color: palette.ink4, textTransform: "capitalize" }}>
													{label}
												</Typography>
												<Typography sx={{ color: palette.ink, fontWeight: 500 }}>{value}</Typography>
											</Box>
										))}
									</Box>
								</Box>
							</Stack>
						</Box>
					</Stack>
				)}
			</Box>
		</Box>
	);
};

export default StylesReport;
